import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Webhook,
  Copy,
  CheckCircle,
  Edit3,
  Activity,
  Globe,
  Trash,
  Search,
  Grid3X3,
  List,
  XCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { MetricCard, MetricCardGrid } from "../../widgets/MetricCard";
import Loader from "../../widgets/Loader";
import WebhookForm from "../../components/webhook/WebhookForm";
import WebhookLogsModal from "../../components/webhook/WebhookLogsModal";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import Can from "../../components/auth/Can";
import webhooksService, {
  Webhook as WebhookType,
  WebhookListParams,
  WebhookStats,
  CreateWebhookData,
  UpdateWebhookData,
} from "../../services/webhooks.service";
import Tooltip from "../../widgets/ToolTip";

// Grid View Component
const GridView: React.FC<{
  webhooks: WebhookType[];
  onEdit: (webhook: WebhookType) => void;
  onDelete: (webhook: WebhookType) => void;
  onViewLogs: (webhook: WebhookType) => void;
}> = ({ webhooks, onEdit, onDelete, onViewLogs }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {webhooks.map((webhook) => (
      <WebhookCard
        key={webhook._id}
        webhook={webhook}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewLogs={onViewLogs}
      />
    ))}
  </div>
);

// List View Component
const ListView: React.FC<{
  webhooks: WebhookType[];
  onEdit: (webhook: WebhookType) => void;
  onDelete: (webhook: WebhookType) => void;
  onViewLogs: (webhook: WebhookType) => void;
}> = ({ webhooks, onEdit, onDelete, onViewLogs }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Webhook
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                URL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {webhooks.map((webhook) => (
              <tr key={webhook._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mr-3">
                      <Webhook className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {webhook.webhook_name}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {webhook.webhook_description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    {webhook.webhook_method}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      webhook.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {webhook.is_active ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Globe className="w-3 h-3 text-gray-400" />
                    <span className="truncate max-w-xs">
                      {webhook.webhook_url}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(webhook.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content="View logs">
                      <button
                        onClick={() => onViewLogs(webhook)}
                        className="text-purple-600 hover:text-purple-900 p-1 rounded"
                        type="button"
                      >
                        <Activity className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Can permission="integrations:edit">
                    <Tooltip content="Edit">
                      <button
                        onClick={() => onEdit(webhook)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        type="button"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    </Can>
                    <Can permission="integrations:delete">
                    <Tooltip content="Delete">
                      <button
                        onClick={() => onDelete(webhook)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        type="button"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    </Can>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WebhookCard: React.FC<{
  webhook: WebhookType;
  onEdit: (webhook: WebhookType) => void;
  onDelete: (webhook: WebhookType) => void;
  onViewLogs: (webhook: WebhookType) => void;
}> = ({ webhook, onEdit, onDelete, onViewLogs }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
  };

  const [copied, setCopied] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
      {/* Webhook Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
              <Webhook className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">
                {webhook.webhook_name}
              </h3>
              <p className="text-xs text-gray-500">{webhook.webhook_method}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {webhook.is_active ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {webhook.webhook_description}
        </p>
      </div>

      {/* Webhook Details */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Globe className="w-4 h-4" />
          <span className="truncate">{webhook.webhook_url}</span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {formatDate(webhook.createdAt)}
          </span>
          <div className="flex items-center gap-2">
            <Tooltip content={copied ? "Copied!" : "Copy URL"}>
              <button
                onClick={() => copyToClipboard(webhook.webhook_url)}
                className={`p-1 rounded transition-colors ${
                  copied
                    ? "text-green-600 hover:text-green-900"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                type="button"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </Tooltip>
            <Tooltip content="View logs">
              <button
                onClick={() => onViewLogs(webhook)}
                className="text-purple-600 hover:text-purple-900 p-1 rounded"
                type="button"
              >
                <Activity className="w-4 h-4" />
              </button>
            </Tooltip>
            <Can permission="integrations:edit">
            <Tooltip content="Edit">
              <button
                onClick={() => onEdit(webhook)}
                className="text-blue-600 hover:text-blue-900 p-1 rounded"
                type="button"
              >
                <Edit3 className="w-4 h-4" />
              </button>
            </Tooltip>
            </Can>
            <Can permission="integrations:delete">
            <Tooltip content="Delete">
              <button
                onClick={() => onDelete(webhook)}
                className="text-red-600 hover:text-red-900 p-1 rounded"
                type="button"
              >
                <Trash className="w-4 h-4" />
              </button>
            </Tooltip>
            </Can>
          </div>
        </div>
      </div>
    </div>
  );
};

const WebHook: React.FC = () => {
  // State management
  const [webhooks, setWebhooks] = useState<WebhookType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingWebhooks, setIsLoadingWebhooks] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [searchFilters, setSearchFilters] = useState({
    webhook_name: "",
    webhook_trigger_event: "",
  });
  const [stats, setStats] = useState<WebhookStats>({
    total_webhooks: 0,
    active_webhooks: 0,
    inactive_webhooks: 0,
    total_deliveries: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Form modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookType | null>(
    null
  );

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<WebhookType | null>(
    null
  );

  // Logs modal state
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedWebhookForLogs, setSelectedWebhookForLogs] =
    useState<WebhookType | null>(null);

  const isInitialMount = useRef(true);

  // Fetch data on mount and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      fetchWebhooks();
      fetchWebhookStats();
      isInitialMount.current = false;
    } else if (Object.values(searchFilters).some((filter) => filter !== "")) {
      const debounceTimer = setTimeout(() => {
        fetchWebhooks();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchWebhooks();
    }
  }, [currentPage, searchFilters]);

  // Fetch webhooks function
  const fetchWebhooks = async () => {
    try {
      setIsLoadingWebhooks(true);
      const params: WebhookListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.webhook_name && {
          webhook_name: searchFilters.webhook_name,
        }),
        ...(searchFilters.webhook_trigger_event && {
          webhook_trigger_event: searchFilters.webhook_trigger_event,
        }),
      };

      const response = await webhooksService.getWebhooks(params);
      setWebhooks(response.data.list);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      toast.error("Failed to load webhooks");
    } finally {
      setIsLoadingWebhooks(false);
    }
  };

  // Fetch webhook statistics
  const fetchWebhookStats = async () => {
    try {
      setIsLoadingStats(true);
      const webhookStats = await webhooksService.getWebhookStats();
      setStats(webhookStats);
    } catch (error) {
      console.error("Error fetching webhook stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Create webhook handler
  const handleCreateWebhook = () => {
    setSelectedWebhook(null);
    setIsFormOpen(true);
  };

  // Edit webhook handler
  const handleEditWebhook = (webhook: WebhookType) => {
    setSelectedWebhook(webhook);
    setIsFormOpen(true);
  };

  // Close form handler
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedWebhook(null);
  };

  // Form submit handler
  const handleFormSubmit = async (
    data: CreateWebhookData | UpdateWebhookData
  ) => {
    setIsLoading(true);
    try {
      if (selectedWebhook) {
        // Update existing webhook
        await webhooksService.updateWebhook(
          selectedWebhook._id,
          data as UpdateWebhookData
        );
        handleCloseForm();
        toast.success(`Webhook "${data.webhook_name}" updated successfully!`);
        await fetchWebhooks();
        await fetchWebhookStats();
      } else {
        // Create new webhook
        await webhooksService.createWebhook(data as CreateWebhookData);
        handleCloseForm();
        toast.success(`Webhook "${data.webhook_name}" created successfully!`);
        await fetchWebhooks();
        await fetchWebhookStats();
      }
    } catch (error: any) {
      console.error("Error saving webhook:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          selectedWebhook
            ? "Failed to update webhook"
            : "Failed to create webhook"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation handler
  const handleDeleteClick = (webhook: WebhookType) => {
    setWebhookToDelete(webhook);
    setShowDeleteConfirm(true);
  };

  // Logs modal handler
  const handleViewLogs = (webhook: WebhookType) => {
    setSelectedWebhookForLogs(webhook);
    setShowLogsModal(true);
  };

  // Close logs modal handler
  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setSelectedWebhookForLogs(null);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (webhookToDelete) {
      try {
        await webhooksService.deleteWebhook(webhookToDelete._id);
        setWebhookToDelete(null);
        setShowDeleteConfirm(false);
        toast.success(
          `Webhook "${webhookToDelete.webhook_name}" deleted successfully!`
        );
        await fetchWebhooks();
        await fetchWebhookStats();
      } catch (error: any) {
        console.error("Error deleting webhook:", error);
        if (error.response?.data?.error?.error_description) {
          toast.error(error.response.data.error.error_description);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete webhook");
        }
      }
    }
  };

  // Cancel delete handler
  const handleCancelDelete = () => {
    setWebhookToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Search handler
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Webhooks</h1>
                <p className="text-xs text-slate-600">
                  Manage and monitor your webhooks for real-time event
                  notifications
                </p>
              </div>
              <div className="flex gap-3">
                <Can permission="integrations:create">
                <button
                  onClick={handleCreateWebhook}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create Webhook
                </button>
                </Can>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <MetricCardGrid>
          <MetricCard
            title="Total Webhooks"
            value={isLoadingStats ? "..." : stats.total_webhooks}
            icon={Webhook}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
          />
          <MetricCard
            title="Active Webhooks"
            value={isLoadingStats ? "..." : stats.active_webhooks}
            icon={Activity}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
          />
          <MetricCard
            title="Inactive Webhooks"
            value={isLoadingStats ? "..." : stats.inactive_webhooks}
            icon={XCircle}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
          />
          <MetricCard
            title="Total Deliveries"
            value={isLoadingStats ? "..." : stats.total_deliveries}
            icon={CheckCircle}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
          />
        </MetricCardGrid>

        {/* Search Filters */}

        <div className="bg-white rounded-lg border border-gray-200 mb-6 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Search Filters
            </h3>

            <div className="flex items-center gap-2 self-end md:self-auto">
              <Tooltip content="Grid view">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "grid"
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  type="button"
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
              </Tooltip>

              <Tooltip content="List view">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "list"
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  type="button"
                >
                  <List className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            {/* Webhook Name Search */}
            <div className="relative w-full h-10 flex items-center">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by webhook name..."
                className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg 
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={searchFilters.webhook_name}
                onChange={(e) =>
                  handleSearchChange("webhook_name", e.target.value)
                }
              />
              {searchFilters.webhook_name && (
                <button
                  type="button"
                  onClick={() => handleSearchChange("webhook_name", "")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 
                  flex items-center justify-center w-6 h-6"
                  aria-label="Clear webhook name search"
                >
                  <Tooltip content="Clear search">
                  <X className="w-4 h-4" />
                </Tooltip>
                </button>
              )}
            </div>

            {/* Trigger Event Search */}
            <div className="relative w-full h-10 flex items-center">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by trigger event..."
                className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg 
                     focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={searchFilters.webhook_trigger_event}
                onChange={(e) =>
                  handleSearchChange("webhook_trigger_event", e.target.value)
                }
              />
              {searchFilters.webhook_trigger_event && (
                <button
                  type="button"
                  onClick={() =>
                    handleSearchChange("webhook_trigger_event", "")
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 
                  flex items-center justify-center w-6 h-6"
                  aria-label="Clear trigger event search"
                >
                  <Tooltip content="Clear search">
                  <X className="w-4 h-4" />
                </Tooltip>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Webhooks Content */}
        {isLoadingWebhooks ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        ) : webhooks.length > 0 ? (
          <>
            {viewMode === "grid" ? (
              <GridView
                webhooks={webhooks}
                onEdit={handleEditWebhook}
                onDelete={handleDeleteClick}
                onViewLogs={handleViewLogs}
              />
            ) : (
              <ListView
                webhooks={webhooks}
                onEdit={handleEditWebhook}
                onDelete={handleDeleteClick}
                onViewLogs={handleViewLogs}
              />
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> webhooks
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No webhooks found
            </h3>
            <p className="text-gray-500 mb-6">
              {Object.values(searchFilters).some((filter) => filter !== "")
                ? "No webhooks match your current search criteria. Try adjusting your filters."
                : "Get started by creating your first webhook configuration."}
            </p>
            <Can permission="integrations:create">
            <button
              onClick={handleCreateWebhook}
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Create Webhook
            </button>
            </Can>
          </div>
        )}
      </div>

      {/* Webhook Form Modal */}
      <WebhookForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        webhook={selectedWebhook}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
      />

      {/* Webhook Logs Modal */}
      {selectedWebhookForLogs && (
        <WebhookLogsModal
          isOpen={showLogsModal}
          onClose={handleCloseLogsModal}
          webhook={selectedWebhookForLogs}
        />
      )}

      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Webhook"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the webhook <strong>${webhookToDelete?.webhook_name}</strong>? This action cannot be undone and may affect your application's event notifications.`}
        iconName="warning"
      />
    </div>
  );
};

export default WebHook;
